import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { $cardListStore, $profileFeatureStore } from '@lib/store';
import { FormatUtils, PaletteUtils, RoutePaths } from '@shared/utils';
import { CardStatus } from '@shared/api/rest/cards';
import { Tabs, TabsItem } from '@widgets';
import { CardBalance } from '../cardBalance';
import { CardGridViewItem } from '../cardGridViewItem';
import { ColorsEnum, NoData, Pagination, Spinner, Table, TableCellType, Text } from '@shared/widgets';
import { useRouteHook } from '@shared/hooks';

const COLUMNS = ['Cardholder', 'Card', 'Type', 'Product', 'status', 'Limit', 'Account', 'Currency'];

export interface ICardsTableProps {
  listType: boolean;
  currency?: string;
  accountId?: number;
  accountIds?: number[];
}

export const CardsTable = (props: ICardsTableProps) => {
  const { navigate } = useRouteHook();

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const cardListStore = useUnit($cardListStore.store);
  const cards = cardListStore.data?.data;
  const total = cardListStore.data?.total || 0;

  const [status, setStatus] = useState<CardStatus>();
  const [index, setIndex] = useState<number>(1);
  const [count, setCount] = useState<number>(10);

  useEffect(() => {
    $cardListStore.events.update({
      AccountId: props.accountId,
      Currency: props.currency,
      Status: status,
      Count: count,
      FromIndex: (index - 1) * count,
      ReturnTotalCount: true,
    });
  }, [index, count, status, props.accountId, props.currency]);

  const filteredCards = useMemo(() => {
    if (!props.accountIds) return cards;
    return cards?.filter((card) => props.accountIds?.includes(card.accountId));
  }, [cards, props.accountIds]);

  if (cardListStore.loading) return <Spinner />;
  return (
    <div className='pb-12'>
      <div className='flex items-center justify-between flex-nowrap'>
        <Tabs>
          <TabsItem text='All' active={status == undefined} withBackground onClick={() => setStatus(undefined)} />
          {Object.entries(CardStatus).map((s, i) =>
            s[1] === CardStatus.UNDEFINED ? null : (
              <TabsItem key={i} text={s[1]} active={status === s[0]} withBackground onClick={() => setStatus(s[0] as CardStatus)} />
            ),
          )}
        </Tabs>
      </div>
      {filteredCards && filteredCards.length > 0 ? (
        <>
          {props.listType ? (
            <Table
              isScrollable
              columns={COLUMNS}
              items={filteredCards.map((card) => [
                {
                  type: TableCellType.Custom,
                  value: (
                    <div className='flex items-center min-w-0 shrink-0'>
                      <div className='w-8 h-8 mr-3 text-sm flex flex-none items-center justify-center overflow-hidden rounded-full relative select-none'>
                        <div className='bg-primary w-full h-full min-w-0 min-h-0 text-soft-black dark:text-white rounded flex items-center justify-center relative z-[1]'>
                          <Text weight='medium' size='xl' color={PaletteUtils.getColor(ColorsEnum.Black, ColorsEnum.White)}>
                            {card.cardHolder?.split('')[0]}
                          </Text>
                        </div>
                      </div>
                      <div className='py-0.5 px-0 min-w-0 flex-1'>
                        <Text className='overflow-hidden text-ellipsis' size='sm'>
                          {card.cardHolder}
                        </Text>
                      </div>
                    </div>
                  ),
                },
                { type: TableCellType.Text, value: FormatUtils.toSliceNumber(card.cardNumber) },
                { type: TableCellType.Text, value: card.cardType },
                { type: TableCellType.Text, value: card.productDisplayName },
                { type: TableCellType.Text, value: card.status },
                { type: TableCellType.Custom, value: <CardBalance id={card.cardId} /> },
                { type: TableCellType.Text, value: FormatUtils.toSliceNumber(card.accountNumber) },
                { type: TableCellType.Text, value: card.currency.toUpperCase() },
              ])}
              onClick={(rowIndex) => {
                const cardId = filteredCards.find((_, index) => rowIndex === index)?.cardId;
                const viewCardDetails = profileFeatureStore.data?.cardFlows?.find((feature) => feature.cardId === cardId)?.viewCardDetails;
                if (viewCardDetails === false) return;
                navigate(RoutePaths.card.onNavigate(cardId!));
              }}
            />
          ) : (
            <div className='pt-5 rounded-tl-none rounded-tr-2 rounded-br-2 rounded-bl-2 bg-white dark:bg-soft-black flex flex-wrap'>
              {filteredCards.map((card, idx) => {
                const viewCardDetails = profileFeatureStore.data?.cardFlows?.find((feature) => feature.cardId === card.cardId)?.viewCardDetails;
                return (
                  <Link key={idx} to={RoutePaths.card.onNavigate(card.cardId)} onClick={(e) => viewCardDetails === false && e.preventDefault()}>
                    <CardGridViewItem card={card} />
                  </Link>
                );
              })}
            </div>
          )}
          <Pagination
            className='mt-6'
            countItemPage={count}
            onChangeCountItemPage={setCount}
            onChangePage={setIndex}
            currentPage={index}
            totalItemCount={total}
          />
        </>
      ) : (
        <NoData className='py-16 bg-white dark:bg-soft-black' text='No related records' />
      )}
    </div>
  );
};
